import { useAuthentication } from "../authentication/authentication-hook";
import { useEffect, useMemo, useState } from "react";
import { IonLoading } from "@ionic/react";
import { PermissionContext } from "../contexts/PermissionContext";
import {
  userIsCleaner,
  userIsFieldWorker,
  userIsOwnerManaged,
  userIsSelfEmployed
} from "../dataStructures/user/predicates";

const DEFAULT_ROLES = {
  isCleaner: false,
  isFieldWorker: false,
  isSelfEmployed: false,
  isOwnerManaged: false,
}

const DEFAULT_PERMISSIONS = {
  canViewJobs: false,
  canViewInvoices: false,
  canViewEstimates: false,
  canCreateNewJobs: false,
  canViewArrivalsReport: false,
};

export function PermissionProvider({ children }) {
  const { user, isAuthenticated } = useAuthentication();

  const [ roles, setRoles ] = useState(() => DEFAULT_ROLES);
  const [ permissions, setPermissions ] = useState(() => DEFAULT_PERMISSIONS);

  const [ loading, setLoading ] = useState(true);

  const value = useMemo(() => ({
    permissions,
    roles,
  }), [ loading, permissions, roles, isAuthenticated, user?.id ]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setRoles(DEFAULT_ROLES);
      setPermissions(DEFAULT_PERMISSIONS);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchPermissions = async () => {
      if (cancelled) {
        return;
      }

      try {
        setLoading(true);

        const isFieldWorker = await userIsFieldWorker(user);
        const isOwnerManaged = await userIsOwnerManaged(user);

        const isCleaner = userIsCleaner(user);
        const isSelfEmployed = userIsSelfEmployed(user);

        if (cancelled) {
          return;
        }

        setRoles({
          isCleaner,
          isFieldWorker,
          isOwnerManaged,
          isSelfEmployed,
        });

        setPermissions({
          canCreateNewJobs: !isOwnerManaged,

          canViewJobs: !isOwnerManaged,
          canViewEstimates: !isCleaner && !isOwnerManaged,
          canViewInvoices: isCleaner && !isOwnerManaged,
          canViewArrivalsReport: isSelfEmployed && !isOwnerManaged,
        });
      } catch (error) {
        // Don't crash.
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPermissions();

    return () => {
      cancelled = true;
    };
  }, [ isAuthenticated, user?.id ]);

  return (
    <PermissionContext.Provider value={ value }>
      { loading
        ? <IonLoading isOpen message="Authorising..." spinner="crescent" />
        : children }
    </PermissionContext.Provider>
  )
}
